import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import { api } from '@/api/client'
import { useCompanies, useDeleteCompany } from '@/api/hooks'
import type { Company } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner } from '@/components/common'
import { CompanyDialog } from './CompanyDialog'

/**
 * The businesses on the platform.
 *
 * <p>A company is the line everything else is drawn inside: its customers,
 * menus, prices, orders and books belong to it and are invisible from any
 * other. Most people see one row here - their own - and are editing their
 * letterhead. Whoever runs the platform sees them all and is the only one who
 * can add or remove one.</p>
 */
export function CompanyTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const { data: companies, error } = useCompanies()
  const remove = useDeleteCompany()

  const [editing, setEditing] = useState<Company | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirming, setConfirming] = useState<Company | null>(null)
  const [backingUp, setBackingUp] = useState(false)
  const [backupError, setBackupError] = useState<unknown>(null)

  const writable = canWrite('org')
  // Setting a company up is a different job from running one: it is the only
  // permission that reaches past your own company.
  const platformAdmin = can('platform.admin')
  const rows = companies ?? []

  const downloadBackup = async () => {
    setBackingUp(true)
    setBackupError(null)
    try {
      await api.download('/api/v1/platform/db-backup', undefined, 'catering-backup.sql')
    } catch (error) {
      setBackupError(error)
    } finally {
      setBackingUp(false)
    }
  }

  return (
    <>
      <ErrorBanner error={error ?? remove.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {platformAdmin
            ? 'Every business on this platform. Each keeps its own masters, numbering and books.'
            : 'Your company, as it appears on quotations, orders and invoices.'}
        </Typography>
        {platformAdmin ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New company
          </Button>
        ) : null}
      </Stack>

      {rows.length === 0 ? (
        <EmptyState message="No companies yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={110}>Code</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Place</TableCell>
              <TableCell width={190}>GSTIN</TableCell>
              <TableCell width={150}>Status</TableCell>
              <TableCell align="right" width={110}>
                &nbsp;
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isOwn = row.id === ownCompanyId
              return (
                <TableRow key={row.id} hover sx={{ opacity: row.active ? 1 : 0.6 }}>
                  <TableCell>
                    <Typography variant="body2">{row.code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.name}</Typography>
                    {row.legalName && row.legalName !== row.name ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.legalName}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.gstin ? (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {row.gstin}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Not registered
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        size="small"
                        color={row.active ? 'success' : 'default'}
                        label={row.active ? 'Active' : 'Inactive'}
                      />
                      {isOwn ? <Chip size="small" variant="outlined" label="Yours" /> : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {writable ? (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => setEditing(row)}
                          aria-label={`Edit ${row.name}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {platformAdmin ? (
                      <Tooltip
                        title={
                          isOwn
                            ? 'This is the company you are signed in to'
                            : 'Delete. Only possible while it holds nothing.'
                        }
                      >
                        {/* A span, so the tooltip still shows on a disabled button. */}
                        <span>
                          <IconButton
                            size="small"
                            disabled={isOwn || remove.isPending}
                            onClick={() => setConfirming(row)}
                            aria-label={`Delete ${row.name}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        Everything except the user list is kept separately per company — customers, menus, packages,
        quotations, orders, invoices, numbering and settings. People are one list across the
        platform, each mapped to the company they work for and the outlets they may see, which is
        set on the <strong>Access</strong> screen.
      </Alert>

      {platformAdmin ? (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Database backup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The whole database, every company on this installation included, as one plain SQL
              file — the same backup the nightly job takes. There is no per-company version of
              this: the data is not split into separate databases, so a download here is a
              platform administrator's document, not any one company's.
            </Typography>
            <ErrorBanner error={backupError} />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={backingUp}
              onClick={() => void downloadBackup()}
            >
              {backingUp ? 'Preparing the backup…' : 'Download SQL backup'}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <CompanyDialog open={creating} onClose={() => setCreating(false)} />
      <CompanyDialog open={Boolean(editing)} company={editing} onClose={() => setEditing(null)} />

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {confirming?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the company along with the outlet and services it was created with. It is
            refused if the company holds anything that has to be kept — a customer, an enquiry, a
            quotation, an order, an invoice or a payment — or if anyone is still mapped to it. Set
            it to Inactive instead in that case.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() =>
              confirming &&
              remove.mutate(confirming.id, {
                // Left open when it is refused, so the reason is read here
                // rather than behind a closed dialog.
                onSuccess: () => setConfirming(null),
              })
            }
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
